'use client';

// Full roster of every registered user, searchable, click through to their profile. Surfaced
// on /live as a "Svi članovi" tab alongside the room list (owner request, 2026-09-08; widened
// to all registered users 2026-10-01 to match live_open()).
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Loader2, MessageCircle, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/lib/i18n/LanguageContext';
import { getOrCreateDm } from '@/lib/liveChat';

type Member = { userId: string; username: string; displayName: string | null; image: string | null };

export default function MembersDirectory() {
  const { user, session } = useAuth();
  const { t } = useLang();
  const router = useRouter();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [messaging, setMessaging] = useState<string | null>(null);

  const onMessage = async (userId: string) => {
    setMessaging(userId);
    try {
      const roomId = await getOrCreateDm(userId);
      router.push(`/messages?room=${roomId}`);
    } catch (e: any) {
      window.alert(e?.message || t('Could not open the conversation.'));
    } finally {
      setMessaging(null);
    }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const headers: Record<string, string> = {};
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
    fetch('/api/community/members', { headers, credentials: 'include', cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : { members: [] }))
      .then((d) => { if (alive) setMembers(d.members ?? []); })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [session]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => m.username.toLowerCase().includes(q) || (m.displayName ?? '').toLowerCase().includes(q));
  }, [members, query]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 pt-3 pb-2 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Search members…')}
            className="input-base w-full pl-9 pr-3 py-2 text-sm"
            aria-label={t('Search members…')}
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={22} className="animate-spin" style={{ color: 'var(--accent)' }} />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-center py-12" style={{ color: 'var(--text-muted)' }}>{t('No members found.')}</p>
        ) : (
          <div className="py-1">
            {filtered.map((m) => (
              <div
                key={m.userId}
                className="flex items-center gap-2 px-4 py-2.5 transition-colors duration-150"
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-sunken)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
              >
                <Link href={`/profile/${encodeURIComponent(m.username)}`} className="flex items-center gap-3 min-w-0 flex-1">
                  {m.image ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={m.image} alt={m.username} className="w-9 h-9 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                      {m.username[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>@{m.username}</div>
                    {m.displayName && <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{m.displayName}</div>}
                  </div>
                </Link>
                {user && m.userId !== user.id ? (
                  <button
                    onClick={() => void onMessage(m.userId)}
                    disabled={messaging === m.userId}
                    className="p-2 rounded-lg shrink-0 disabled:opacity-40"
                    style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}
                    title={`${t('Message')} @${m.username}`}
                    aria-label={`${t('Message')} @${m.username}`}
                  >
                    {messaging === m.userId ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
